// src/pages/SourcesPage.js
import { useState, useEffect } from 'react';
import api from '../utils/api';

const TIER_LABELS = {
  T1: { label: 'Core Tenders',        color: 'var(--green)',  desc: 'Government procurement portals' },
  T2: { label: 'Mining & Industrial', color: 'var(--accent)', desc: 'Mining company and EPC supplier portals' },
  T3: { label: 'Construction Intel',  color: 'var(--blue)',   desc: 'Pre-tender project intelligence' },
  T4: { label: 'Indigenous',          color: 'var(--purple)', desc: 'Indigenous procurement and partnerships' },
  T5: { label: 'Industry News',       color: '#8b949e',       desc: 'News and project announcements' },
};

const STATUS_STYLES = {
  active:            { bg: 'var(--green-dim)',  color: 'var(--green)',  label: '✓ Active' },
  accessible:        { bg: 'var(--blue-dim)',   color: 'var(--blue)',   label: '◉ Accessible' },
  register_required: { bg: 'var(--orange-dim)', color: 'var(--orange)', label: '⚠ Register Req.' },
  blocked:           { bg: 'var(--red-dim)',    color: 'var(--red)',    label: '✕ Blocked' },
  paid:              { bg: 'var(--purple-dim)', color: 'var(--purple)', label: '$ Paid' },
  error:             { bg: 'var(--red-dim)',    color: 'var(--red)',    label: '✕ Error' },
};

const TYPE_LABELS = {
  government_portal: 'Gov Portal',
  industry_platform: 'Industry',
  mining_company:    'Mining Co.',
  epc_company:       'EPC/Construction',
  indigenous:        'Indigenous',
  news_intelligence: 'News/Intel',
};

export default function SourcesPage() {
  const [sources, setSources] = useState([]);
  const [stats, setStats] = useState({});
  const [byTier, setByTier] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterTier, setFilterTier] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    api.get('/sources').then(data => {
      setSources(data.sources || []);
      setStats(data.stats || {});
      setByTier(data.by_tier || []);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = sources.filter(s => {
    if (filterTier && s.tier !== filterTier) return false;
    if (filterStatus && s.status !== filterStatus) return false;
    if (search && !s.name.toLowerCase().includes(search.toLowerCase()) &&
        !s.url.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group by tier
  const grouped = {};
  filtered.forEach(s => {
    if (!grouped[s.tier]) grouped[s.tier] = [];
    grouped[s.tier].push(s);
  });

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Sources</div>
          <div style={{ fontSize:'0.85rem', color:'var(--text-muted)', marginTop:'0.2rem' }}>
            {stats.total} sources tracked — {stats.active} active, {stats.accessible} accessible
          </div>
        </div>
      </div>

      {/* Tier summary cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(5, 1fr)', gap:'0.75rem', marginBottom:'1.5rem' }}>
        {byTier.map(t => {
          const tier = TIER_LABELS[t.tier] || {};
          return (
            <button key={t.tier} onClick={() => setFilterTier(filterTier === t.tier ? '' : t.tier)}
              style={{
                background: filterTier === t.tier ? tier.color + '22' : 'var(--bg-surface)',
                border: `1px solid ${filterTier === t.tier ? tier.color : 'var(--border)'}`,
                borderRadius: 'var(--radius-md)', padding:'0.75rem',
                cursor:'pointer', textAlign:'left', transition:'all 0.15s',
              }}>
              <div style={{ fontSize:'0.65rem', color: tier.color || 'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontWeight:700, marginBottom:'0.3rem' }}>
                {t.tier} — {tier.label}
              </div>
              <div style={{ display:'flex', alignItems:'baseline', gap:'0.4rem' }}>
                <span style={{ fontSize:'1.4rem', fontWeight:800, color:'var(--text-primary)' }}>{t.count}</span>
                <span style={{ fontSize:'0.75rem', color:'var(--green)' }}>{t.active} active</span>
              </div>
              <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', marginTop:'0.2rem' }}>{tier.desc}</div>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:'0.75rem', marginBottom:'1.25rem', flexWrap:'wrap', alignItems:'center' }}>
        <input type="text" placeholder="Search sources…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ maxWidth:240 }} />
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={{ maxWidth:180 }}>
          <option value="">All Statuses</option>
          {Object.entries(STATUS_STYLES).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
        </select>
        {(filterTier || filterStatus || search) && (
          <button className="btn btn-ghost" onClick={() => { setFilterTier(''); setFilterStatus(''); setSearch(''); }}>
            Clear
          </button>
        )}
        <div style={{ marginLeft:'auto', fontSize:'0.82rem', color:'var(--text-muted)' }}>
          {filtered.length} sources
        </div>
      </div>

      {/* Sources by tier */}
      {loading ? (
        <div style={{ padding:'3rem', textAlign:'center', color:'var(--text-muted)' }}>Loading…</div>
      ) : (
        Object.entries(grouped).sort().map(([tier, tierSources]) => {
          const tierInfo = TIER_LABELS[tier] || {};
          return (
            <div key={tier} style={{ marginBottom:'1.5rem' }}>
              <div style={{
                display:'flex', alignItems:'center', gap:'0.75rem',
                marginBottom:'0.75rem', paddingBottom:'0.5rem',
                borderBottom:`2px solid ${tierInfo.color || 'var(--border)'}`,
              }}>
                <span style={{ fontFamily:'var(--font-display)', fontWeight:800, fontSize:'0.9rem', color: tierInfo.color, letterSpacing:'0.05em' }}>
                  {tier}
                </span>
                <span style={{ fontWeight:600, fontSize:'0.9rem' }}>{tierInfo.label}</span>
                <span style={{ fontSize:'0.8rem', color:'var(--text-muted)' }}>{tierInfo.desc}</span>
                <span style={{ marginLeft:'auto', fontSize:'0.8rem', color:'var(--text-muted)' }}>{tierSources.length} sources</span>
              </div>

              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(300px, 1fr))', gap:'0.75rem' }}>
                {tierSources.map(s => {
                  const statusStyle = STATUS_STYLES[s.status] || STATUS_STYLES.error;
                  return (
                    <div key={s.id} className="card" style={{ padding:'0.875rem' }}>
                      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'0.5rem', marginBottom:'0.5rem' }}>
                        <div style={{ flex:1 }}>
                          <div style={{ fontWeight:600, fontSize:'0.875rem' }}>{s.name}</div>
                          <a href={s.url} target="_blank" rel="noreferrer"
                            style={{ fontSize:'0.72rem', color:'var(--text-muted)', wordBreak:'break-all' }}>
                            {s.url.replace('https://','').replace('http://','').slice(0,50)}
                          </a>
                        </div>
                        <span style={{ background: statusStyle.bg, color: statusStyle.color, fontSize:'0.68rem', fontWeight:700, padding:'0.15rem 0.5rem', borderRadius:10, whiteSpace:'nowrap', flexShrink:0 }}>
                          {statusStyle.label}
                        </span>
                      </div>

                      <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap', alignItems:'center' }}>
                        <span style={{ fontSize:'0.7rem', background:'var(--bg-hover)', color:'var(--text-muted)', padding:'0.1rem 0.45rem', borderRadius:8 }}>
                          {TYPE_LABELS[s.type] || s.type}
                        </span>
                        {s.coverage && (
                          <span style={{ fontSize:'0.7rem', color:'var(--text-muted)' }}>{s.coverage}</span>
                        )}
                        {s.tender_count > 0 && (
                          <span style={{ fontSize:'0.7rem', color:'var(--accent)', marginLeft:'auto', fontWeight:600 }}>
                            {s.tender_count} tenders
                          </span>
                        )}
                      </div>

                      {s.notes && (
                        <div style={{ fontSize:'0.72rem', color:'var(--text-muted)', marginTop:'0.4rem', borderTop:'1px solid var(--border)', paddingTop:'0.4rem' }}>
                          {s.notes}
                        </div>
                      )}

                      {s.last_scraped && (
                        <div style={{ fontSize:'0.68rem', color:'var(--text-muted)', marginTop:'0.3rem' }}>
                          Last scraped: {new Date(s.last_scraped).toLocaleDateString('en-CA')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}
